
import Vue from "vue";
import { Component } from "vue-property-decorator";
import AliasState from "@/app/dto/AliasStateEnum";
import Factor from "@/app/dto/FactorEnum";
import IAllowedFactor from "@/app/dto/IAllowedFactor";
import IAllowedFactorsResponse from "@/app/dto/IAllowedFactorsResponse";
import IInitPasswordRenewalResponse from "@/app/dto/IInitPasswordRenewalResponse";
import ProcessEnum from "@/app/dto/ProcessEnum";
import ProcessResultKey from "@/app/dto/ProcessResultKeyEnum";
import IApiError from "@/app/model/IApiError";
import RouteName from "@/app/router/NameEnum";
import AliasService from "@/app/service/alias";
import AuthorizationService from "@/app/service/authorization";
import { getContactPhoneNumber } from "@/app/utils/EnvConfig";

@Component
export default class PasswordSet extends Vue {
    private customerUuid: string = "";

    private passwordModel: { oldPass: string; newPass: string; confirmPass: string } = {
        oldPass: "",
        newPass: "",
        confirmPass: ""
    };

    private minimumPasswordLength: number = 8;

    private get passwordLabels(): Object {
        return {
            oldPass: "",
            newPass: this.$localize("pwd.set.new.password.placeholder"),
            confirmPass: this.$localize("pwd.set.new.password.check.placeholder"),
            submit: this.$localize("pwd.set.new.password.submit"),
            toggleTooltipTextOn: "",
            toggleTooltipTextOff: ""
        };
    }

    private get validationMessages(): Object {
        return {
            oldPass: {
                additional: "",
                error: "",
                success: ""
            },
            newPass: {
                additional: this.$localize("pwd.set.new.password.rules", this.minimumPasswordLength),
                error: this.$localize("pwd.set.new.password.invalid"),
                success: this.$localize("pwd.set.new.password.secured")
            },
            confirmPass: {
                additional: "",
                error: this.$localize("pwd.set.new.password.equalTo"),
                success: ""
            }
        };
    }

    private onSubmit(): void {
        AuthorizationService.getAllowedFactorsExternal(this.customerUuid, ProcessEnum.PASSWORD_RENEWAL)
            .then((response: IAllowedFactorsResponse) => {
                if (this.hasOtp(response)) {
                    this.$router.push({
                        name: RouteName.PASSWORD_OTP,
                        params: { customerUuid: this.customerUuid, password: this.passwordModel.newPass }
                    });
                } else {
                    this.routeToError();
                }
            })
            .catch((error: IApiError) => {
                this.processAuthorizationKeyError(error);
            });
    }

    private verifyAlias(): void {
        if (this.$route.query.alias) {
            AliasService.verify(this.$route.query.alias as string)
                .then((response: IInitPasswordRenewalResponse) => {
                    this.customerUuid = response.customerUuid;
                })
                .catch((error: IApiError) => {
                    this.processVerifyAliasError(error);
                });
        } else {
            this.routeToError();
        }
    }

    private hasOtp(response: IAllowedFactorsResponse): boolean {
        return !!response.data!!.find((f: IAllowedFactor) => f.factors!! && f.factors?.includes(Factor.OTP));
    }

    private processAuthorizationKeyError(error: IApiError) {
        if (error.status == ProcessResultKey.ACCOUNT_BLOCKED) {
            let errorHeading: string = this.$localize("error.account.title");
            let errorMessage: string = this.$localize("error.account.blocked.message");
            this.routeToError({ heading: errorHeading, message: errorMessage });
        }
        this.routeToError();
    }

    private processVerifyAliasError(error: IApiError): void {
        let errorHeading: string = "";
        let errorMessage: string = "";
        let vfmError: boolean = false;

        switch (error.status) {
            case AliasState.PUBLIC_ALIAS_EXPIRED:
                errorHeading = this.$localize("error.alias.outdated.title");
                errorMessage = this.$localize("error.alias.outdated.message", [
                    getContactPhoneNumber()
                ]);
                break;
            case AliasState.PUBLIC_ALIAS_INVALID:
                errorHeading = this.$localize("error.alias.expired.title");
                errorMessage = this.$localize("error.alias.expired.message", [
                    getContactPhoneNumber()
                ]);
                break;
            case ProcessResultKey.ACCOUNT_NOT_ACTIVE:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.account.not_active.message");
                break;
            case ProcessResultKey.ACCOUNT_NOT_FOUND:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.account.not_found.message");
                break;
            case ProcessResultKey.ACCOUNT_BLOCKED:
                errorHeading = this.$localize("error.account.title");
                errorMessage = this.$localize("error.account.blocked.message");
                break;
            case AliasState.VERIFIED_MOBILE_NOT_FOUND:
                vfmError = true;
                break;
        }

        if (vfmError) {
            this.$router.push({ name: RouteName.PASSWORD_VERIFY_PHONE });
        } else {
            this.routeToError({ heading: errorHeading, message: errorMessage });
        }
    }

    private routeToError(params?: { heading: string; message: string }) {
        this.$router.push({ name: RouteName.PASSWORD_ERROR, params });
    }

    created() {
        this.verifyAlias();
    }
}
